import { InjectionToken } from '@angular/core';

import {
  NotificationActionPreset,
  NotificationPosition,
  NotificationTheme,
  NotificationType,
} from './notification.enum';

/** Optional button rendered under the toast message (Retry / Refresh / Undo / …). */
export interface NotificationAction {
  /** Visible button text. */
  label: string;
  /** Overrides the accessible name when `label` alone is ambiguous. */
  ariaLabel?: string;
  /** Material font ligature shown before the label, e.g. `refresh`. */
  icon?: string;
  /**
   * Invoked on click / Enter / Space. Also emitted through
   * `NotificationRef.onAction()` so callers can subscribe instead.
   */
  handler?: () => void;
  /** Close the toast once the handler has run. Defaults to `true`. */
  dismissOnAction?: boolean;
}

/** Everything a caller may specify when raising a notification. */
export interface NotificationConfig {
  /** Bold lead-in, e.g. "No data found." Omit for a message-only toast. */
  title?: string;
  /** Body copy. Required — this is the part screen readers announce. */
  message: string;
  /**
   * Material font ligature replacing the built-in severity icon. Set to `null`
   * to render no icon at all.
   */
  icon?: string | null;
  /** Auto-dismiss delay in ms. `0` keeps the toast up until it is dismissed. */
  duration?: number;
  /** Anchor point for this toast's stack. */
  position?: NotificationPosition;
  /** Render the close (×) button. Defaults to `true`. */
  dismissible?: boolean;
  /** Freeze the auto-dismiss timer while hovered or focused. Defaults to `true`. */
  pauseOnHover?: boolean;
  /** Dismiss when the toast body itself is clicked. Defaults to `false`. */
  tapToDismiss?: boolean;
  /** Action button, or one of the presets via {@link notificationAction}. */
  action?: NotificationAction;
  /** Extra classes applied to the toast host element. */
  panelClass?: string | string[];
  /**
   * Collapses duplicates: raising a toast whose `key` is already on screen
   * restarts that toast's timer instead of stacking a second copy.
   */
  key?: string;
  /** Arbitrary payload echoed back on the {@link NotificationRef}. */
  data?: unknown;
  /** Overrides the politeness level inferred from {@link NotificationType}. */
  ariaLive?: 'polite' | 'assertive' | 'off';
  /** Overrides the ARIA role inferred from {@link NotificationType}. */
  role?: 'alert' | 'status';
}

/**
 * The tail options of `success()` / `error()` / `warning()` / `info()`, whose
 * message and title are already positional arguments.
 */
export type NotificationOptions = Omit<NotificationConfig, 'message' | 'title'>;

/** Application-wide fallbacks, overridable through {@link NOTIFICATION_DEFAULTS}. */
export interface NotificationDefaults {
  duration: number;
  position: NotificationPosition;
  dismissible: boolean;
  pauseOnHover: boolean;
  tapToDismiss: boolean;
  theme: NotificationTheme;
  /** Maximum concurrent toasts per position; the oldest is evicted past this. */
  maxStack: number;
  /** Clear every toast on a successful router navigation. */
  dismissOnNavigation: boolean;
  /** Accessible name of the live region wrapping each stack. */
  containerAriaLabel: string;
  /** Accessible name of the × button. */
  closeAriaLabel: string;
}

/**
 * A resolved notification: the caller's config merged over the defaults, with
 * every rendering decision already made. This is what the components consume.
 */
export interface NotificationInstance {
  /** Unique, stable across the toast's lifetime — used as the `ngFor` trackBy. */
  readonly id: string;
  readonly type: NotificationType;
  readonly message: string;
  readonly duration: number;
  readonly position: NotificationPosition;
  readonly dismissible: boolean;
  readonly pauseOnHover: boolean;
  readonly tapToDismiss: boolean;
  readonly ariaLive: 'polite' | 'assertive' | 'off';
  readonly role: 'alert' | 'status';
  readonly title?: string;
  readonly icon?: string | null;
  readonly action?: NotificationAction;
  readonly panelClass?: string | string[];
  readonly key?: string;
  readonly data?: unknown;
}

export const NOTIFICATION_DEFAULTS = new InjectionToken<Partial<NotificationDefaults>>(
  'NOTIFICATION_DEFAULTS'
);

/** Position handed to each container component through its portal injector. */
export const NOTIFICATION_POSITION = new InjectionToken<NotificationPosition>(
  'NOTIFICATION_POSITION'
);

export const DEFAULT_NOTIFICATION_DEFAULTS: NotificationDefaults = {
  duration: 5000,
  position: NotificationPosition.TopRight,
  dismissible: true,
  pauseOnHover: true,
  tapToDismiss: false,
  // The portal has no dark theme, so `Auto` would flip the toasts to their dark
  // palette on an OS-level dark preference while the page behind stays light.
  theme: NotificationTheme.Light,
  maxStack: 5,
  dismissOnNavigation: false,
  containerAriaLabel: 'Notifications',
  closeAriaLabel: 'Dismiss notification',
};

const ACTION_PRESETS: Readonly<Record<NotificationActionPreset, NotificationAction>> = {
  [NotificationActionPreset.Retry]: { label: 'Retry', icon: 'replay' },
  [NotificationActionPreset.Refresh]: { label: 'Refresh', icon: 'refresh' },
  [NotificationActionPreset.Undo]: { label: 'Undo', icon: 'undo' },
};

/**
 * Builds one of the preset action buttons.
 *
 * ```ts
 * this.notify.error('We couldn\'t generate your export.', 'Export failed.', {
 *   action: notificationAction(NotificationActionPreset.Retry, () => this.export()),
 * });
 * ```
 */
export function notificationAction(
  preset: NotificationActionPreset,
  handler?: () => void,
  overrides: Partial<NotificationAction> = {}
): NotificationAction {
  return { ...ACTION_PRESETS[preset], handler, ...overrides };
}
